import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'
import { X } from 'lucide-react'

import { Combobox } from '@/components/Combobox'
import { TOAST_OPTIONS, triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useAuthStore } from '@/features/auth/authStore'
import { useCompanyList } from '@/features/company'
import { ERoleType } from '@/features/user-management/constants'
import { ProjectStatus, ProjectType } from '@/schema'
import {
  DEFAULT_GET_ALL_LIMIT,
  ERROR_CODES,
  MAX_TEXT_LENGTH,
  SortDirection,
} from '@/utils/constants'

import { cn } from '../../../utils'
import { MAX_REPORTING_EVENTS, PROJECT_TYPE_OPTIONS } from '../constants'
import { ReportingEvent } from '../types'
import { useCreateProject, useProjectsStore, useUpdateProject } from '..'

import { ReportingEventsInput } from './ReportingEventsInput'

interface ApiErrorResponse {
  error?: {
    code?: string
    message?: string
    type?: string
  }
  message?: string
}

interface ProjectFormData {
  company_id: string
  project_name: string
  project_type: string
  status: string
  reporting_event_ids: string[]
}

const ProjectDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    editingProject,
    dialogOpen: open,
    clearEditingProject,
    setDialogOpen,
    contextCompanyId,
  } = useProjectsStore()

  const roles = useAuthStore((state) => state.roles)
  const getCompaniesFromRoles = useAuthStore(
    (state) => state.getCompaniesFromRoles
  )

  const isProjectLead = useMemo(() => {
    const hasProjectLeadRole = roles?.some(
      (role) => role.role_name === ERoleType.PROJECT_LEAD
    )
    const hasOrgAdminRole = roles?.some(
      (role) => role.role_name === ERoleType.ORGANIZATION_ADMIN
    )
    // Org admins have full access, so they should not be treated as project leads
    return hasProjectLeadRole && !hasOrgAdminRole
  }, [roles])

  const isEditMode = useMemo(() => !!editingProject, [editingProject])

  // Track selected reporting events locally
  const [selectedReportingEvents, setSelectedReportingEvents] = useState<
    ReportingEvent[]
  >([])

  // Track the last value that caused an API error
  const [lastErrorValue, setLastErrorValue] = useState<string>('')

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<ProjectFormData>({
    defaultValues: {
      company_id: '',
      project_name: '',
      project_type: ProjectType.STANDALONE,
      status: '',
      reporting_event_ids: [],
    },
  })

  const { data: companiesResponse, isLoading: isLoadingCompanies } =
    useCompanyList({
      rowsPerPage: DEFAULT_GET_ALL_LIMIT,
      sort: { column: 'name', direction: SortDirection.ASC },
      enabled: !isProjectLead,
    })

  const companiesData = useMemo(() => {
    if (isProjectLead) {
      // For Project Lead, get companies from roles instead of API
      return getCompaniesFromRoles()
    }
    return companiesResponse?.data || []
  }, [isProjectLead, companiesResponse, getCompaniesFromRoles])

  const createMutation = useCreateProject()
  const updateMutation = useUpdateProject()

  const initialValues = useMemo(() => {
    if (isEditMode && editingProject) {
      return {
        company_id: editingProject.company_id,
        project_name: editingProject.project_name,
        project_type: editingProject.project_type,
        status: editingProject.status || '',
        reporting_event_ids:
          editingProject.reporting_events?.map((event) => event.id) || [],
      }
    }
    return null
  }, [isEditMode, editingProject])

  useEffect(() => {
    if (isEditMode && editingProject) {
      setValue('company_id', editingProject.company_id)
      setValue('project_name', editingProject.project_name)
      setValue('project_type', editingProject.project_type)
      setValue('status', editingProject.status || '')
      setValue(
        'reporting_event_ids',
        editingProject.reporting_events?.map((event) => event.id) || []
      )
      setSelectedReportingEvents(editingProject.reporting_events || [])
    } else if (!isEditMode && open && contextCompanyId && !isLoadingCompanies) {
      setValue('company_id', contextCompanyId)
    }
  }, [
    isEditMode,
    editingProject,
    setValue,
    open,
    contextCompanyId,
    isLoadingCompanies,
  ])

  useEffect(() => {
    if (!open) {
      reset({
        company_id: '',
        project_name: '',
        project_type: ProjectType.ASCEND_SPARK,
        status: '',
        reporting_event_ids: [],
      })
      setSelectedReportingEvents([])
      setLastErrorValue('')
      createMutation.reset()
      updateMutation.reset()
      if (isEditMode) {
        clearEditingProject()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset])

  const onSubmit = (data: ProjectFormData) => {
    // Validate all required fields per AC
    if (!data.company_id) {
      setError('company_id', {
        type: 'manual',
        message: t('project.dialog.validation.companyRequired'),
      })
      return
    }

    if (!data.project_name?.trim()) {
      setError('project_name', {
        type: 'manual',
        message: t('project.dialog.validation.nameRequired'),
      })
      return
    }

    if (!data.status) {
      setError('status', {
        type: 'manual',
        message: t('project.dialog.validation.statusRequired'),
      })
      return
    }

    if (!data.reporting_event_ids || data.reporting_event_ids.length === 0) {
      setError('reporting_event_ids', {
        type: 'manual',
        message: t('project.dialog.validation.reportingEventRequired'),
      })
      return
    }

    if (data.reporting_event_ids.length > MAX_REPORTING_EVENTS) {
      setError('reporting_event_ids', {
        type: 'manual',
        message: t('project.dialog.validation.maxReportingEvents', {
          max: MAX_REPORTING_EVENTS,
        }),
      })
      return
    }

    const formData = {
      company_id: data.company_id,
      project_name: data.project_name.trim(),
      project_type: data.project_type,
      status: data.status,
      reporting_event_ids: data.reporting_event_ids,
    }

    if (isEditMode && editingProject) {
      // Update project
      updateMutation.mutate(
        {
          id: editingProject.id,
          data: formData,
        },
        {
          onSuccess: () => {
            triggerToast(
              'success',
              t('project.dialog.messages.updateSuccess'),
              TOAST_OPTIONS
            )
            setDialogOpen(false)
            clearEditingProject()
          },
          onError: (error: unknown) => {
            const axiosError = error as AxiosError<ApiErrorResponse>
            const errorMessage =
              axiosError.response?.data?.error?.message ||
              axiosError.response?.data?.message ||
              axiosError.message ||
              t('project.dialog.messages.errorGeneric')
            const errorCode = axiosError.response?.data?.error?.code

            if (errorCode === ERROR_CODES.DUPLICATE) {
              setError('project_name', {
                type: 'manual',
                message: errorMessage,
              })
              setLastErrorValue(formData.project_name)
            } else {
              triggerToast('error', errorMessage, TOAST_OPTIONS)
            }
          },
        }
      )
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          triggerToast('success', t('project.dialog.messages.addSuccess'))
          setDialogOpen(false)
        },
        onError: (error: unknown) => {
          const axiosError = error as AxiosError<ApiErrorResponse>
          const errorMessage =
            axiosError.response?.data?.error?.message ||
            axiosError.response?.data?.message ||
            axiosError.message ||
            t('project.dialog.messages.errorGeneric')

          const errorCode = axiosError.response?.data?.error?.code

          if (errorCode === ERROR_CODES.DUPLICATE) {
            setError('project_name', {
              type: 'manual',
              message: errorMessage,
            })
            setLastErrorValue(formData.project_name)
          } else {
            triggerToast('error', errorMessage)
          }
        },
      })
    }
  }

  const isLoading = useMemo(() => {
    return isLoadingCompanies
  }, [isLoadingCompanies])

  const isSaving = useMemo(
    () => createMutation.isPending || updateMutation.isPending,
    [createMutation.isPending, updateMutation.isPending]
  )

  const handleOpenChange = (newOpen: boolean) => {
    setDialogOpen(newOpen)
    if (!newOpen && isEditMode) {
      clearEditingProject()
    }
  }

  // Handler to update both form value and selected events state
  const handleReportingEventsChange = useCallback(
    (newIds: string[], newEvents: ReportingEvent[]) => {
      setValue('reporting_event_ids', newIds)
      setSelectedReportingEvents(newEvents)
    },
    [setValue]
  )

  // Handler for reporting events error state
  const handleReportingEventsError = useCallback(
    (hasError: boolean, message?: string) => {
      if (hasError && message) {
        setError('reporting_event_ids', {
          type: 'manual',
          message,
        })
      } else {
        clearErrors('reporting_event_ids')
      }
    },
    [setError, clearErrors]
  )

  const companyId = watch('company_id')
  const projectName = watch('project_name')
  const projectType = watch('project_type')
  const status = watch('status')
  const reportingEventIds = watch('reporting_event_ids')

  // Clear API error when user changes the project name
  useEffect(() => {
    if (errors.project_name && projectName?.trim() !== lastErrorValue) {
      clearErrors('project_name')
    }
  }, [projectName, errors.project_name, lastErrorValue, clearErrors])

  const hasFormChanged = useMemo(() => {
    if (!isEditMode || !initialValues) {
      return true
    }

    const companyChanged = companyId !== initialValues.company_id
    const nameChanged = projectName?.trim() !== initialValues.project_name
    const typeChanged = projectType !== initialValues.project_type
    const statusChanged = status !== initialValues.status

    const currentEventIds = [...(reportingEventIds || [])].sort((a, b) =>
      String(a).localeCompare(String(b))
    )
    const initialEventIds = [...initialValues.reporting_event_ids].sort(
      (a, b) => String(a).localeCompare(String(b))
    )
    const eventsChanged =
      currentEventIds.length !== initialEventIds.length ||
      currentEventIds.some((id, index) => id !== initialEventIds[index])

    return (
      companyChanged ||
      nameChanged ||
      typeChanged ||
      statusChanged ||
      eventsChanged
    )
  }, [
    isEditMode,
    initialValues,
    companyId,
    projectName,
    projectType,
    status,
    reportingEventIds,
  ])

  const isFormValid = useMemo(() => {
    return (
      companyId &&
      projectName?.trim() &&
      status &&
      reportingEventIds?.length > 0
    )
  }, [companyId, projectName, status, reportingEventIds])

  const isDisableProjectType = useMemo(() => {
    return isEditMode && !editingProject?.can_delete
  }, [isEditMode, editingProject])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] overflow-visible bg-white shadow-xl rounded-2xl p-0 flex flex-col max-h-[90vh]">
          <DialogHeader className="space-y-0.5 px-6 pt-6">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {isEditMode
                ? t('project.dialog.editTitle')
                : t('project.dialog.addTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {isEditMode
                ? t('project.dialog.editDescription')
                : t('project.dialog.addDescription')}
            </DialogDescription>
          </DialogHeader>

          <form
            id="project-form"
            onSubmit={handleSubmit(onSubmit)}
            className="flex-1 px-6 space-y-4 overflow-visible"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label
                  htmlFor="company_id"
                  className="block text-sm font-medium text-tertiary-700"
                >
                  {t('project.dialog.fields.company')}{' '}
                </label>
                <Combobox
                  placeholder={t('project.dialog.placeholders.selectCompany')}
                  options={companiesData.map((company) => ({
                    label: company.name,
                    value: company.id,
                  }))}
                  fullWidth
                  maxHeight={250}
                  value={companyId}
                  onChange={(value) => setValue('company_id', value)}
                  disabled={isLoading || isSaving || !!contextCompanyId}
                  clearable={!contextCompanyId}
                />
                {errors.company_id && (
                  <p className="mt-1 text-xs text-error-600">
                    {errors.company_id.message}
                  </p>
                )}
                {!companiesData.length && !isLoading && (
                  <p className="mt-1 text-xs text-warning-600">
                    {t('project.dialog.warnings.noCompanies')}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="project_name"
                  className="block text-sm font-medium text-tertiary-700"
                >
                  {t('project.dialog.fields.project')}{' '}
                </label>
                <Input
                  id="project_name"
                  type="text"
                  maxLength={MAX_TEXT_LENGTH}
                  placeholder={t('project.dialog.placeholders.enterName')}
                  disabled={isSaving}
                  trailingIcon={
                    <X
                      className={cn(
                        'w-3.5 h-3.5 transition-opacity',
                        projectName ? 'opacity-100 cursor-pointer' : 'opacity-0'
                      )}
                    />
                  }
                  onTrailingIconClick={
                    projectName ? () => setValue('project_name', '') : undefined
                  }
                  {...register('project_name', {
                    required: t('project.dialog.validation.nameRequired'),
                    validate: {
                      notEmpty: (value) =>
                        value.trim().length > 0 ||
                        t('project.dialog.validation.nameRequired'),
                    },
                  })}
                />
                {errors.project_name && (
                  <p className="mt-1 text-xs text-error-600">
                    {errors.project_name.message}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label
                  htmlFor="project_type"
                  className="block text-sm font-medium text-tertiary-700"
                >
                  {t('project.dialog.fields.projectType')}
                </label>
                <Select
                  placeholder={t(
                    'project.dialog.placeholders.selectProjectType'
                  )}
                  options={PROJECT_TYPE_OPTIONS.map((option) => ({
                    label: t(option.label),
                    value: option.value,
                  }))}
                  fullWidth
                  value={projectType}
                  onChange={(value) => setValue('project_type', value)}
                  disabled={isDisableProjectType || isLoading || isSaving}
                />
                {errors.project_type && (
                  <p className="mt-1 text-xs text-error-600">
                    {errors.project_type.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-tertiary-700"
                >
                  {t('project.dialog.fields.status')}{' '}
                </label>
                <Combobox
                  placeholder={t('project.dialog.placeholders.selectStatus')}
                  options={[
                    {
                      label: t('project.status.active'),
                      value: ProjectStatus.ACTIVE,
                    },
                    {
                      label: t('project.status.archived'),
                      value: ProjectStatus.ARCHIVED,
                    },
                  ]}
                  fullWidth
                  maxHeight={200}
                  value={status}
                  onChange={(value) => setValue('status', value)}
                  disabled={isLoading || isSaving}
                  clearable
                />
                {errors.status && (
                  <p className="mt-1 text-xs text-error-600">
                    {errors.status.message}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="reporting_event_ids"
                className="block text-sm font-medium text-tertiary-700"
              >
                {t('project.dialog.fields.reportingEvent')}{' '}
              </label>
              <ReportingEventsInput
                value={reportingEventIds || []}
                onChange={handleReportingEventsChange}
                disabled={isLoading || isSaving}
                selectedEvents={selectedReportingEvents}
                onError={handleReportingEventsError}
              />
              {errors.reporting_event_ids && (
                <p className="mt-1 text-xs text-error-600">
                  {errors.reporting_event_ids.message}
                </p>
              )}
            </div>
          </form>

          <DialogFooter className="gap-3 px-6 pt-4 pb-6 sm:gap-3 shrink-0">
            <Button
              type="button"
              variant="outlined"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-md border-solid bg-white text-[--Colors-Gray-light-700]"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="project-form"
              disabled={
                isLoading ||
                isSaving ||
                !isFormValid ||
                !hasFormChanged ||
                !!errors.project_name
              }
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-md border-solid border-transparent text-white bg-brand-solid hover:bg-brand-solid-hover"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default ProjectDialog
